import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CalendarCheck, CalendarDays, CalendarPlus, Clock } from "lucide-react";
import {
    bookPortalSession,
    getBookingAvailability,
    listBookingSlots,
} from "../../application/portalServices";
import { formatSlotTimeWithZone, toDateKey } from "../../../scheduler";
import type {
    PatientBookingAvailability,
    PortalTimeSlot,
} from "../../domain/types";
import { formatDayChip, formatDayLong, upcomingWorkingDays } from "../../domain/bookingDays";

interface BookSessionSectionProps {
    /** Called once a session is booked, so the page can show it as coming up. */
    onBooked?: () => void;
}

/** Times shown in the therapist's zone; the zone is spelled out once below. */
function slotTime(start: string, timeZone: string): string {
    return new Intl.DateTimeFormat("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
        timeZone,
    }).format(new Date(start));
}

/**
 * Booking entry point in the patient portal.
 *
 * Day, then time, then one button to book — all in view. The days offered are
 * the ones the therapist works, so the patient is not left guessing at a
 * calendar; the times are buttons rather than a closed list, so they are seen
 * before they are chosen.
 *
 * Renders nothing at all unless the therapist has adopted the Scheduler tool
 * and published availability — the backend decides, the portal never guesses.
 */
export default function BookSessionSection({ onBooked }: BookSessionSectionProps) {
    const { t, i18n } = useTranslation();

    const [availability, setAvailability] =
        useState<PatientBookingAvailability | null>(null);
    const [date, setDate] = useState("");
    const [slots, setSlots] = useState<PortalTimeSlot[]>([]);
    const [selected, setSelected] = useState<string | null>(null);
    const [loadingSlots, setLoadingSlots] = useState(false);
    const [slotsError, setSlotsError] = useState(false);
    const [booking, setBooking] = useState(false);
    const [bookingError, setBookingError] = useState<string | null>(null);
    const [booked, setBooked] = useState<string | null>(null);
    const dateInputRef = useRef<HTMLInputElement>(null);

    const timezone = availability?.timezone ?? null;

    const workingDays = useMemo(
        () =>
            timezone
                ? upcomingWorkingDays(new Date(), availability?.active_weekdays ?? [], timezone)
                : [],
        [availability, timezone]
    );

    useEffect(() => {
        getBookingAvailability()
            .then((data) => {
                setAvailability(data);
                if (data.booking_enabled && data.timezone) {
                    // Open on a day the therapist actually works, so the first
                    // thing the patient sees is times rather than an empty list.
                    const [first] = upcomingWorkingDays(
                        new Date(),
                        data.active_weekdays ?? [],
                        data.timezone,
                        1
                    );
                    setDate(first ?? toDateKey(new Date(), data.timezone));
                }
            })
            .catch(() =>
                setAvailability({
                    booking_enabled: false,
                    timezone: null,
                    default_duration: null,
                    active_weekdays: [],
                })
            );
    }, []);

    const loadSlots = useCallback(() => {
        if (!date) return;
        setLoadingSlots(true);
        setSlotsError(false);
        listBookingSlots(date)
            .then((data) => setSlots(data.slots))
            .catch(() => setSlotsError(true))
            .finally(() => setLoadingSlots(false));
    }, [date]);

    useEffect(() => {
        // A time belongs to its day: moving to another day drops it.
        setSelected(null);
        setBookingError(null);
        loadSlots();
    }, [loadSlots]);

    if (!availability?.booking_enabled || !timezone) return null;

    // A day picked further out joins the row, selected, so the patient can see
    // which day the times below belong to.
    const days = workingDays.includes(date) || !date
        ? workingDays
        : [...workingDays, date].sort();
    const nextDay = workingDays.find((day) => day > date) ?? null;

    const confirmBooking = () => {
        if (!selected) return;
        setBooking(true);
        setBookingError(null);
        bookPortalSession(selected)
            .then(() => {
                setBooked(selected);
                setSelected(null);
                onBooked?.();
                loadSlots();
            })
            .catch((error: { response?: { status?: number } }) => {
                if (error?.response?.status === 409) {
                    // Someone took it between listing and confirming.
                    setBookingError(t("scheduler.schedule.slotTaken"));
                    setSelected(null);
                    loadSlots();
                } else {
                    setBookingError(t("scheduler.schedule.bookingError"));
                }
            })
            .finally(() => setBooking(false));
    };

    const openPicker = () => {
        const input = dateInputRef.current;
        if (!input) return;
        if (typeof input.showPicker === "function") input.showPicker();
        else input.focus();
    };

    const header = (
        <div className="flex items-center gap-2">
            <h2 className="flex items-center gap-2 text-base font-semibold text-content-primary">
                <CalendarPlus size={18} aria-hidden="true" className="text-brand-500" />
                {t("scheduler.schedule.bookSession")}
            </h2>
            {availability.default_duration && (
                <span className="ml-auto flex items-center gap-1 text-xs text-content-tertiary">
                    <Clock size={12} aria-hidden="true" />
                    {availability.default_duration} {t("scheduler.common.minutes")}
                </span>
            )}
        </div>
    );

    if (booked) {
        return (
            <section className="glass-effect glass-border flex flex-col gap-4 rounded-2xl border p-5 shadow-sm">
                {header}
                <div role="status" className="flex items-start gap-3 rounded-xl bg-green-50 p-4 dark:bg-green-900/20">
                    <CalendarCheck size={20} aria-hidden="true" className="mt-0.5 shrink-0 text-green-600 dark:text-green-400" />
                    <p className="text-sm text-content-primary">
                        {t("portal.booking.booked", {
                            day: formatDayLong(toDateKey(new Date(booked), timezone), i18n.language),
                            time: formatSlotTimeWithZone(booked, timezone),
                        })}
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => setBooked(null)}
                    className="self-start min-h-11 rounded-xl px-4 text-sm font-medium text-brand-700 hover:bg-brand-50 dark:text-brand-300 dark:hover:bg-brand-500/10"
                >
                    {t("portal.booking.bookAnother")}
                </button>
            </section>
        );
    }

    return (
        <section className="glass-effect glass-border flex flex-col gap-4 rounded-2xl border p-5 shadow-sm">
            {header}

            {/* ── Day ─────────────────────────────────────────── */}
            <div
                role="group"
                aria-label={t("portal.booking.chooseDay")}
                // Bleeds to the card edge so the row reads as scrollable on a phone.
                className="-mx-5 flex gap-2 overflow-x-auto px-5 pb-1 [scrollbar-width:none]"
            >
                {days.map((day) => {
                    const isSelected = day === date;
                    return (
                        <button
                            key={day}
                            type="button"
                            aria-pressed={isSelected}
                            onClick={() => setDate(day)}
                            className={`min-h-11 shrink-0 rounded-xl border px-3 text-sm font-medium capitalize transition-colors ${
                                isSelected
                                    ? "border-brand-600 bg-brand-600 text-white"
                                    : "border-gray-200 text-content-secondary hover:border-brand-300 hover:bg-brand-50 dark:border-white/10 dark:hover:bg-brand-500/10"
                            }`}
                        >
                            {formatDayChip(day, i18n.language)}
                        </button>
                    );
                })}
                <button
                    type="button"
                    onClick={openPicker}
                    className="flex min-h-11 shrink-0 items-center gap-1.5 rounded-xl border border-dashed border-gray-300 px-3 text-sm text-content-tertiary hover:border-brand-300 hover:text-content-secondary dark:border-white/15"
                >
                    <CalendarDays size={16} aria-hidden="true" />
                    {t("portal.booking.otherDate")}
                </button>
                <input
                    ref={dateInputRef}
                    type="date"
                    aria-label={t("scheduler.schedule.date")}
                    value={date}
                    min={toDateKey(new Date(), timezone)}
                    onChange={(event) => event.target.value && setDate(event.target.value)}
                    className="sr-only"
                />
            </div>

            {/* ── Time ────────────────────────────────────────── */}
            {loadingSlots ? (
                <div role="status" aria-label={t("scheduler.schedule.loadingSlots")} className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                    {Array.from({ length: 4 }, (_, i) => (
                        <div key={i} className="h-11 animate-pulse rounded-xl bg-gray-200/70 dark:bg-white/5" />
                    ))}
                </div>
            ) : slotsError ? (
                <div role="alert" className="flex items-center gap-3 text-sm">
                    <span className="text-red-600 dark:text-red-400">
                        {t("scheduler.schedule.slotsError")}
                    </span>
                    <button
                        type="button"
                        onClick={loadSlots}
                        className="min-h-11 rounded-lg px-3 font-medium text-brand-700 hover:bg-brand-50 dark:text-brand-300"
                    >
                        {t("scheduler.common.retry")}
                    </button>
                </div>
            ) : slots.length === 0 ? (
                <div className="flex flex-wrap items-center gap-2 rounded-xl bg-surface-sunken px-4 py-3 text-sm text-content-secondary">
                    <span>{t("portal.booking.noSlots")}</span>
                    {nextDay && (
                        <button
                            type="button"
                            onClick={() => setDate(nextDay)}
                            className="min-h-11 rounded-lg px-2 font-medium text-brand-700 hover:underline dark:text-brand-300"
                        >
                            {t("portal.booking.nextDay", { day: formatDayChip(nextDay, i18n.language) })}
                        </button>
                    )}
                </div>
            ) : (
                <div role="group" aria-label={t("portal.booking.times")} className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                    {slots.map((slot) => {
                        const isSelected = slot.start === selected;
                        return (
                            <button
                                key={slot.start}
                                type="button"
                                aria-pressed={isSelected}
                                onClick={() => {
                                    setBookingError(null);
                                    setSelected(slot.start);
                                }}
                                className={`min-h-11 rounded-xl border text-sm font-medium tabular-nums transition-colors ${
                                    isSelected
                                        ? "border-brand-600 bg-brand-600 text-white"
                                        : "border-gray-200 text-content-primary hover:border-brand-300 hover:bg-brand-50 dark:border-white/10 dark:hover:bg-brand-500/10"
                                }`}
                            >
                                {slotTime(slot.start, timezone)}
                            </button>
                        );
                    })}
                </div>
            )}

            <p className="text-xs text-content-tertiary">
                {t("scheduler.calendar.timezoneNote", { timezone })}
            </p>

            {bookingError && (
                <p role="alert" className="text-sm text-red-600 dark:text-red-400">
                    {bookingError}
                </p>
            )}

            {/* ── Book ────────────────────────────────────────── */}
            {selected && (
                <div className="flex flex-col gap-3 rounded-xl border border-brand-200 bg-brand-50/60 p-4 sm:flex-row sm:items-center dark:border-brand-500/30 dark:bg-brand-400/5">
                    <p className="flex-1 text-sm text-content-primary">
                        <span className="font-semibold first-letter:uppercase">
                            {formatDayLong(date, i18n.language)}
                        </span>
                        {" · "}
                        {formatSlotTimeWithZone(selected, timezone)}
                    </p>
                    <button
                        type="button"
                        disabled={booking}
                        onClick={confirmBooking}
                        className="min-h-11 rounded-xl bg-brand-600 px-5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
                    >
                        {booking ? t("scheduler.schedule.booking") : t("portal.booking.confirm")}
                    </button>
                </div>
            )}
        </section>
    );
}
